import React, {
	createContext,
	useState,
	useEffect,
	useContext,
	ReactNode,
} from "react";

interface ThemeContextType {
	theme: "light" | "dark";
	toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
	children: ReactNode;
}

const getInitialTheme = () => {
	if (typeof window !== "undefined") {
		return (localStorage.getItem("theme") as "light" | "dark") || "light";
	}
	return "light";
};

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
	const [theme, setTheme] = useState<"light" | "dark">(getInitialTheme);

	useEffect(() => {
		const localTheme = window.localStorage.getItem("theme") as "light" | "dark";
		if (localTheme) {
			setTheme(localTheme);
		}
	}, []);

	const toggleTheme = () => {
		const newTheme = theme === "light" ? "light" : "light";
		setTheme(newTheme);
		window.localStorage.setItem("theme", newTheme);
	};

	return (
		<ThemeContext.Provider value={{ theme, toggleTheme }}>
			{children}
		</ThemeContext.Provider>
	);
};

export const useTheme = () => {
	const context = useContext(ThemeContext);
	if (context === undefined) {
		throw new Error("useTheme must be used within a ThemeProvider");
	}
	return context;
};
